import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
export function CancellationAccordion() {
  const [openSection, setOpenSection] = useState<string | null>(null);
  const sections = [{
    id: 'web-design-projects',
    title: '1. Web Design & Development Projects',
    content: <>
          <h4 className="font-semibold mb-3" data-id="element-771">1.1 Deposit Payments</h4>
          <p className="mb-4" data-id="element-772">
            All web design and development projects require a non-refundable
            deposit of 50% of the total project cost before work begins. This
            deposit secures your place in our schedule and covers initial
            project setup, planning, and design work.
          </p>
          <h4 className="font-semibold mb-3" data-id="element-773">
            1.2 Cancellation Before Project Commencement
          </h4>
          <p className="mb-4" data-id="element-774">
            If you cancel your project after paying the deposit but before we
            begin work (defined as the date we share the first design concept or
            development environment), we will refund 25% of your deposit. The
            remaining 75% is retained to cover administrative costs, scheduling,
            and preliminary work.
          </p>
          <h4 className="font-semibold mb-3" data-id="element-775">
            1.3 Cancellation During Design Phase
          </h4>
          <p className="mb-3" data-id="element-776">
            If you cancel your project during the design phase (after we've
            shared the first design concept but before development begins), the
            following applies:
          </p>
          <ul className="space-y-2 ml-4 mb-4" data-id="element-777">
            <li className="flex items-start" data-id="element-778">
              <span className="mr-2" data-id="element-779">•</span>
              <span data-id="element-780">
                Within 3 days of receiving the first design concept: 25% of the
                deposit may be refunded
              </span>
            </li>
            <li className="flex items-start" data-id="element-781">
              <span className="mr-2" data-id="element-782">•</span>
              <span data-id="element-783">
                After 3 days of receiving the first design concept: No refund of
                the deposit
              </span>
            </li>
            <li className="flex items-start" data-id="element-784">
              <span className="mr-2" data-id="element-785">•</span>
              <span data-id="element-786">
                Any additional payments made beyond the deposit will be refunded
                minus the cost of work completed
              </span>
            </li>
          </ul>
          <h4 className="font-semibold mb-3" data-id="element-787">
            1.4 Cancellation During Development Phase
          </h4>
          <p className="mb-4" data-id="element-788">
            If you cancel your project during the development phase (after we've
            begun building the website), no refund will be provided for the
            deposit or any milestone payments made for completed work. We will
            provide you with all completed work up to the cancellation date.
          </p>
          <h4 className="font-semibold mb-3" data-id="element-789">1.5 Project Abandonment</h4>
          <p className="mb-3" data-id="element-790">
            If a project remains inactive for more than 30 days due to lack of
            client communication or failure to provide required materials, we
            reserve the right to consider the project abandoned. In such cases:
          </p>
          <ul className="space-y-2 ml-4" data-id="element-791">
            <li className="flex items-start" data-id="element-792">
              <span className="mr-2" data-id="element-793">•</span>
              <span data-id="element-794">No refunds will be issued for payments made</span>
            </li>
            <li className="flex items-start" data-id="element-795">
              <span className="mr-2" data-id="element-796">•</span>
              <span data-id="element-797">
                The project may be removed from our active project list
              </span>
            </li>
            <li className="flex items-start" data-id="element-798">
              <span className="mr-2" data-id="element-799">•</span>
              <span data-id="element-800">
                A project reactivation fee of £150 may apply if you wish to
                resume the project
              </span>
            </li>
          </ul>
        </>
  }, {
    id: 'website-care-plans',
    title: '2. Website Maintenance Packages & Ongoing Services',
    content: <>
          <h4 className="font-semibold mb-3" data-id="element-801">2.1 Monthly Packages</h4>
          <p className="mb-3" data-id="element-802">For monthly Website Maintenance Packages:</p>
          <ul className="space-y-2 ml-4 mb-4" data-id="element-803">
            <li className="flex items-start" data-id="element-804">
              <span className="mr-2" data-id="element-805">•</span>
              <span data-id="element-806">
                You may cancel at any time with 30 days' written notice
              </span>
            </li>
            <li className="flex items-start" data-id="element-807">
              <span className="mr-2" data-id="element-808">•</span>
              <span data-id="element-809">
                No refunds are provided for the current billing period
              </span>
            </li>
            <li className="flex items-start" data-id="element-810">
              <span className="mr-2" data-id="element-811">•</span>
              <span data-id="element-812">
                Services will continue until the end of the paid period
              </span>
            </li>
          </ul>
          <h4 className="font-semibold mb-3" data-id="element-813">2.2 Annual Packages</h4>
          <p className="mb-3" data-id="element-814">
            For annual Website Maintenance Packages paid in advance:
          </p>
          <ul className="space-y-2 ml-4 mb-4" data-id="element-815">
            <li className="flex items-start" data-id="element-816">
              <span className="mr-2" data-id="element-817">•</span>
              <span data-id="element-818">
                Cancellations within the first 30 days: 75% refund of the annual
                fee
              </span>
            </li>
            <li className="flex items-start" data-id="element-819">
              <span className="mr-2" data-id="element-820">•</span>
              <span data-id="element-821">
                Cancellations after 30 days but before 6 months: 40% refund of
                the annual fee
              </span>
            </li>
            <li className="flex items-start" data-id="element-822">
              <span className="mr-2" data-id="element-823">•</span>
              <span data-id="element-824">Cancellations after 6 months: No refund</span>
            </li>
          </ul>
          <h4 className="font-semibold mb-3" data-id="element-825">2.3 Domain Names & Hosting</h4>
          <p data-id="element-826">
            Domain name registrations and hosting services are non-refundable
            once the domain has been registered or the hosting account has been
            set up. Domain transfers to another provider can be arranged at no
            additional cost.
          </p>
        </>
  }, {
    id: 'change-requests',
    title: '3. Change Requests & Project Modifications',
    content: <>
          <h4 className="font-semibold mb-3" data-id="element-827">3.1 Scope Changes</h4>
          <p className="mb-4" data-id="element-828">
            Changes to project scope after the initial agreement may incur
            additional charges. Significant changes to project requirements may
            require a revised quote and timeline.
          </p>
          <h4 className="font-semibold mb-3" data-id="element-829">3.2 Design Revisions</h4>
          <p className="mb-4" data-id="element-830">
            Our packages include a specified number of design revision rounds.
            Additional revision rounds beyond those included in your package
            will be charged at our standard hourly rate of £75/hour.
          </p>
          <h4 className="font-semibold mb-3" data-id="element-831">3.3 Content Changes</h4>
          <p data-id="element-832">
            Changes to content after it has been implemented on the website may
            incur additional charges if they require significant reformatting or
            redesign of pages.
          </p>
        </>
  }, {
    id: 'refund-process',
    title: '4. Refund Process',
    content: <>
          <h4 className="font-semibold mb-3" data-id="element-833">4.1 Requesting a Refund</h4>
          <p className="mb-4" data-id="element-834">
            To request a refund or cancellation, please contact us in writing at{' '}
            <a href="mailto:accounts@bright-pixxel.com" className="text-[#0C1B26] hover:underline" data-id="element-835">
              accounts@bright-pixxel.com
            </a>
            . Your request should include your project details and reason for
            cancellation.
          </p>
          <h4 className="font-semibold mb-3" data-id="element-836">4.2 Refund Processing</h4>
          <p className="mb-4" data-id="element-837">
            Approved refunds will be processed within 14 business days to the
            original payment method used for the purchase.
          </p>
          <h4 className="font-semibold mb-3" data-id="element-838">4.3 Dispute Resolution</h4>
          <p data-id="element-839">
            If you have a dispute regarding a cancellation or refund, please
            contact us directly to resolve the issue before initiating a
            chargeback or third-party dispute. We aim to address all concerns
            fairly and promptly.
          </p>
        </>
  }, {
    id: 'termination',
    title: '5. Termination by Bright Pixxel Studios',
    content: <>
          <p className="mb-3" data-id="element-840">
            We reserve the right to terminate a project or service at our
            discretion if:
          </p>
          <ul className="space-y-2 ml-4 mb-4" data-id="element-841">
            <li className="flex items-start" data-id="element-842">
              <span className="mr-2" data-id="element-843">•</span>
              <span data-id="element-844">The client breaches the terms of service or contract</span>
            </li>
            <li className="flex items-start" data-id="element-845">
              <span className="mr-2" data-id="element-846">•</span>
              <span data-id="element-847">
                The client engages in abusive behavior toward our staff
              </span>
            </li>
            <li className="flex items-start" data-id="element-848">
              <span className="mr-2" data-id="element-849">•</span>
              <span data-id="element-850">
                The project involves content that violates our acceptable use
                policy
              </span>
            </li>
            <li className="flex items-start" data-id="element-851">
              <span className="mr-2" data-id="element-852">•</span>
              <span data-id="element-853">The client fails to make scheduled payments</span>
            </li>
          </ul>
          <p data-id="element-854">
            In cases where we terminate service, refunds will be handled on a
            case-by-case basis.
          </p>
        </>
  }, {
    id: 'contact',
    title: '6. Contact Us',
    content: <>
          <p className="mb-4" data-id="element-855">
            If you have any questions about this Cancellation Policy, please
            contact us at:
          </p>
          <p className="mb-2" data-id="element-856">
            Email:{' '}
            <a href="mailto:accounts@bright-pixxel.com" className="text-[#0C1B26] hover:underline" data-id="element-857">
              accounts@bright-pixxel.com
            </a>
          </p>
          <p data-id="element-858">Phone: +44 (0) 123 456 7890</p>
        </>
  }];
  const toggleSection = (sectionId: string) => {
    setOpenSection(openSection === sectionId ? null : sectionId);
  };
  return <div className="space-y-4" data-id="element-859">
      {sections.map(section => <div key={section.id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden" data-id="element-860">
          <button onClick={() => toggleSection(section.id)} className={`w-full p-4 flex items-center justify-between text-left transition-colors ${openSection === section.id ? 'bg-[#0C1B26]/10 dark:bg-[#0C1B26]/20' : 'bg-white dark:bg-[#1E1E1E]'}`} data-id="element-861">
            <h3 className="text-lg font-semibold" data-id="element-862">{section.title}</h3>
            <ChevronDownIcon size={20} className={`transition-transform ${openSection === section.id ? 'rotate-180' : ''}`} data-id="element-863" />
          </button>
          <AnimatePresence data-id="element-864">
            {openSection === section.id && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.3
        }} className="overflow-hidden" data-id="element-865">
                <div className="p-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700" data-id="element-866">
                  <div className="text-gray-600 dark:text-gray-300" data-id="element-867">
                    {section.content}
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>)}
    </div>;
}